import api from './apiClient';
import type { ApiResponse } from 'shared/types/api';

export interface Form {
  _id: string;
  code?: string;
  name: string;
  description?: string;
  category: string;
  requiredFor?: string[];
  tags?: string[];
  isGlobal?: boolean;
  company?: string;
  metadata?: {
    estimatedCompletionTime?: number;
    requiresApproval?: boolean;
    approvalRoles?: string[];
    attachmentRequired?: boolean;
    expirationDays?: number;
  };
  sections: FormSection[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  order: number;
  fields: FormField[];
}

export interface FormField {
  id: string;
  label: string;
  name: string;
  type: 'checkbox' | 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'textarea' | 'radio' | 'signature' | 'file';
  required: boolean;
  order: number;
  options?: Array<{ value: string; label: string }> | string[];
  validation?: {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: string;
  };
  conditional?: {
    showIf: {
      field: string;
      operator: 'equals' | 'notEquals' | 'greaterThan' | 'lessThan';
      value: any;
    };
  };
  placeholder?: string;
  helperText?: string;
}

export interface FormCreateData {
  name: string;
  description?: string;
  category: string;
  requiredFor?: string[];
  tags?: string[];
  isGlobal?: boolean;
  company?: string;
  metadata?: {
    estimatedCompletionTime?: number;
    requiresApproval?: boolean;
    approvalRoles?: string[];
    attachmentRequired?: boolean;
    expirationDays?: number;
  };
  sections: FormSection[];
}

export interface FormStats {
  totalForms: number;
  activeForms: number;
  byCategory: Array<{
    category: string;
    count: number;
  }>;
  byWorkPermitCategory: Array<{
    category: string;
    count: number;
  }>;
}

class FormsApiService {
  // Listar formularios (global or company-scoped)
  async getAllForms(params?: {
    page?: number;
    isGlobal?: boolean;
    limit?: number;
    category?: string;
    isActive?: boolean;
    companyId?: string;
  }): Promise<ApiResponse<Form[]>> {
    try {
      const queryParams: Record<string, any> = {};

      if (params?.page) queryParams.page = params.page;
      if (params?.limit) queryParams.limit = params.limit;
      if (params?.category) queryParams.category = params.category;
      if (params?.isActive !== undefined) queryParams.isActive = params.isActive;
      if (params?.isGlobal !== undefined) queryParams.isGlobal = params.isGlobal;

      //  NEW: If companyId is provided, use company-scoped endpoint
      let endpoint = '/forms';
      if (params?.companyId) {
        endpoint = `/forms/companies/${params.companyId}`;
      }

      const result = await api.get(endpoint, queryParams);

      return {
        success: true,
        data: result.data || result.forms || [],
        pagination: result.pagination
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener formularios'
      };
    }
  }

  // Buscar formularios
  async searchForms(query: string, limit?: number): Promise<ApiResponse<Form[]>> {
    try {
      const queryParams: Record<string, any> = { q: query };
      if (limit) queryParams.limit = limit;

      const result = await api.get('/forms/templates/search', queryParams);

      return {
        success: true,
        data: result.data || result.forms || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al buscar formularios'
      };
    }
  }

  // Obtener formulario por ID
  async getFormById(formId: string): Promise<ApiResponse<Form>> {
    try {
      const result = await api.get(`/forms/${formId}`);

      return {
        success: true,
        data: result.data || result.form
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener formulario'
      };
    }
  }

  // Crear formulario
  async createForm(formData: FormCreateData): Promise<ApiResponse<Form>> {
    try {
      const result = await api.post('/forms', formData);

      return {
        success: true,
        data: result.data || result.form,
        message: 'Formulario creado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al crear formulario'
      };
    }
  }

  // Actualizar formulario
  async updateForm(formId: string, formData: Partial<FormCreateData>): Promise<ApiResponse<Form>> {
    try {
      const result = await api.put(`/forms/${formId}`, formData);

      return {
        success: true,
        data: result.data || result.form,
        message: 'Formulario actualizado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar formulario'
      };
    }
  }

  // Duplicar formulario
  async duplicateForm(formId: string, newName: string): Promise<ApiResponse<Form>> {
    try {
      const result = await api.post(`/forms/templates/${formId}/duplicate`, { name: newName });

      return {
        success: true,
        data: result.data || result.form,
        message: 'Formulario duplicado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al duplicar formulario'
      };
    }
  }

  // Desactivar formulario (soft delete)
  async deleteForm(formId: string): Promise<ApiResponse<void>> {
    try {
      await api.delete(`/forms/${formId}`);

      return {
        success: true,
        message: 'Formulario desactivado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al desactivar formulario'
      };
    }
  }

  // Obtener formularios por categoría
  async getFormsByCategory(category: string, includeInactive = false): Promise<ApiResponse<Form[]>> {
    try {
      const result = await api.get(`/forms/templates/category/${category}`, { includeInactive });

      return {
        success: true,
        data: result.data || result.forms || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener formularios por categoría'
      };
    }
  }

  // Obtener formularios por categoría de permiso de trabajo
  async getFormsByWorkPermitCategory(category: string): Promise<ApiResponse<Form[]>> {
    try {
      const result = await api.get(`/forms/templates/work-permit-category/${category}`);

      return {
        success: true,
        data: result.data || result.forms || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener formularios'
      };
    }
  }

  // Obtener estadísticas de formularios
  async getFormStats(): Promise<ApiResponse<FormStats>> {
    try {
      const result = await api.get('/forms/stats');

      return {
        success: true,
        data: result.data || result.stats
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener estadísticas'
      };
    }
  }
}

export const formsApi = new FormsApiService();
